import { useMutation, useQueryClient } from '@tanstack/react-query'

import type { Moment } from 'moment'
import useAlertService from '../services/AlertService'
import { SingleAlertType } from '@/app/constants/singleAlertType'
import { toast } from '@/lib/toasts/api'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

type UseMutationAlertParams = {
  companyId: string
  alertId: string
  driverId: string
  vehicleId: string
  eventTime: Moment | string
  remindTime: Moment | string | null
  type: SingleAlertType
  text: string
  notRemindAnymore: boolean
}

export function useMutationAlert(companyId: string) {
  const queryClient = useQueryClient()
  const alertService = useAlertService()
  const { t } = useLocalize()

  return useMutation({
    mutationFn: async (params: UseMutationAlertParams) => {
      const result = await alertService.addEditAlert({
        ...params,
        companyId,
        showLoading: false, // React Query handles loading state
      })
      if (!result) {
        throw new Error('Failed to save alert')
      }
      return result
    },
    onError: (error) => {
      console.error('Error saving alert:', error)
      toast.error({
        text: t('alerts.save-error'),
      })
    },
    onSuccess: (_, variables) => {
      // Invalidate alerts queries to refetch
      queryClient.invalidateQueries({
        queryKey: ['alerts', companyId],
      })
      toast.success({
        text: variables.alertId === '0' ? t('alerts.added-success') : t('alerts.updated-success'),
      })
    },
  })
}

